import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, money, pct, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDisplayCurrency } from "@/context/CurrencyContext";
import { ListForSaleDialog } from "@/pages/Secondary";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { LayoutDashboard, FileCheck2, Users2, Wallet, LogOut, Building2, ChevronRight, CheckCircle2, Clock, XCircle, RefreshCcw } from "lucide-react";

const CHART_COLORS = ["#C5A059", "#0F5132", "#0B192C", "#8B6B2E", "#3B5F8A", "#B38F46"];

export function InvestorLayout({ children, active }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = [
    { to: "/portfolio", label: "Portfolio", icon: LayoutDashboard, id: "portfolio" },
    { to: "/kyc", label: "KYC & Documents", icon: FileCheck2, id: "kyc" },
    { to: "/statements", label: "Rental Statements", icon: Wallet, id: "statements" },
    { to: "/referrals", label: "Referral Hub", icon: Users2, id: "referrals" },
    { to: "/withdrawals", label: "Withdrawals", icon: Wallet, id: "withdrawals" },
  ];
  if (!user || user === false) return null; // ProtectedRoute handles redirect / loader
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex">
      <aside className="hidden lg:flex flex-col w-72 bg-[#0B192C] text-white p-6">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-[#C5A059]" /></div>
          <span className="font-serif text-xl">OwnNest</span>
        </Link>
        <div className="mb-6 pb-6 border-b border-white/10">
          <div className="overline text-[#C5A059]">Investor</div>
          <div className="mt-1 truncate">{user.name}</div>
          <div className="text-xs text-white/60 truncate">{user.email}</div>
          <KycBadge status={user.kyc_status} />
        </div>
        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`sidebar-${l.id}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${active === l.id ? "bg-[#C5A059] text-[#0B192C]" : "text-white/80 hover:bg-white/5"}`}>
              <l.icon className="h-4 w-4" />{l.label}
            </Link>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/"); }} className="mt-4 flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="sidebar-logout">
          <LogOut className="h-4 w-4" />Sign out
        </button>
      </aside>
      <main className="flex-1 p-6 lg:p-10">{children}</main>
    </div>
  );
}

function KycBadge({ status }) {
  const map = { approved: { c: "bg-emerald-500/20 text-emerald-300", i: CheckCircle2 }, pending: { c: "bg-amber-500/20 text-amber-300", i: Clock }, rejected: { c: "bg-red-500/20 text-red-300", i: XCircle }, not_started: { c: "bg-white/10 text-white/80", i: Clock } };
  const m = map[status] || map.not_started;
  return <div className={`inline-flex items-center gap-1 mt-3 px-2 py-1 rounded-full text-xs ${m.c}`}><m.i className="h-3 w-3" />KYC: {status.replace("_", " ")}</div>;
}

export function Portfolio() {
  const [data, setData] = useState(null);
  const [myListings, setMyListings] = useState([]);
  const { currency: displayCur, convert } = useDisplayCurrency();
  const load = () => {
    api.get("/portfolio").then((r) => setData(r.data)).catch(() => {});
    api.get("/secondary/listings/my").then((r) => setMyListings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const cancelListing = async (id) => {
    try { await api.post(`/secondary/listings/${id}/cancel`); toast.success("Listing cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <InvestorLayout active="portfolio">
      <div className="overline">Portfolio</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#0B192C] mt-2">Your holdings.</h1>
      {!data ? <div className="mt-10 text-slate-500">Loading…</div> : (
        <>
          <div className="grid md:grid-cols-4 gap-4 mt-8">
            {[
              { l: `Total Invested (${displayCur})`, v: money(convert(data.total_invested, "USD"), displayCur), tid: "kpi-invested" },
              { l: `Monthly Rental (${displayCur})`, v: money(convert(data.monthly_rental, "USD"), displayCur), tid: "kpi-monthly" },
              { l: "Active Holdings", v: data.active_holdings, tid: "kpi-holdings" },
              { l: `Wallet Balance (${displayCur})`, v: money(convert(data.wallet_balance, "USD"), displayCur), tid: "kpi-wallet" },
            ].map((m) => (
              <div key={m.l} className="bg-white border border-[#E5DFD5] rounded-xl p-6" data-testid={m.tid}>
                <div className="overline">{m.l}</div>
                <div className="metric-value text-3xl mt-2 text-[#0B192C]">{m.v}</div>
              </div>
            ))}
          </div>

          {data.by_currency && data.by_currency.length > 1 && (
            <div className="mt-4 bg-white border border-[#E5DFD5] rounded-xl p-4" data-testid="by-currency-strip">
              <div className="overline mb-2">Native Currency Subtotals</div>
              <div className="flex flex-wrap gap-4">
                {data.by_currency.map((c) => (
                  <div key={c.currency} className="text-sm">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#F3EFEA] text-[#8B6B2E] mr-2">{c.currency}</span>
                    <span className="font-mono text-[#0B192C]">{money(c.invested, c.currency)}</span>
                    <span className="text-slate-500 ml-1">invested</span>
                    <span className="text-slate-400 mx-2">·</span>
                    <span className="font-mono text-[#0F5132]">{money(c.monthly, c.currency)}</span>
                    <span className="text-slate-500 ml-1">/ month</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.holdings.length > 0 && (
            <div className="grid md:grid-cols-2 gap-5 mt-8">
              <div className="bg-white border border-[#E5DFD5] rounded-xl p-6" data-testid="chart-by-city">
                <div className="overline">Breakdown by City</div>
                <div className="h-64 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.breakdown_by_city} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {data.breakdown_by_city.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} />
                      <Legend verticalAlign="bottom" height={30} iconSize={8} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-[#E5DFD5] rounded-xl p-6" data-testid="chart-by-type">
                <div className="overline">Breakdown by Property Type</div>
                <div className="h-64 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.breakdown_by_type} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" />
                      <XAxis dataKey="name" fontSize={11} stroke="#64748B" />
                      <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => money(v)} />
                      <Bar dataKey="value" fill="#C5A059" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <h2 className="font-serif text-2xl text-[#0B192C] mt-12 mb-4">Holdings</h2>
          {data.holdings.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5DFD5] rounded-xl p-10 text-center">
              <p className="text-slate-600">You don't own any fractions yet.</p>
              <Link to="/marketplace"><Button className="btn-gold mt-4 rounded-full">Browse Marketplace</Button></Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {data.holdings.map((h) => (
                <div key={h.investment_id} className="bg-white border border-[#E5DFD5] rounded-xl p-5" data-testid={`holding-${h.investment_id}`}>
                  <div className="flex gap-4">
                    <img src={h.hero_image} alt="" className="h-24 w-32 object-cover rounded-lg" />
                    <div className="flex-1">
                      <div className="font-medium text-[#0B192C] flex items-center justify-between gap-2">
                        <span>{h.property_title}</span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#F3EFEA] text-[#8B6B2E]">{h.currency || "INR"}</span>
                      </div>
                      <div className="text-xs text-slate-500">{h.location}</div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div><div className="text-slate-500">Units</div><div className="font-mono">{h.units}</div></div>
                        <div><div className="text-slate-500">Own %</div><div className="font-mono">{pct(h.ownership_percent, 3)}</div></div>
                        <div><div className="text-slate-500">Monthly</div><div className="font-mono text-[#0F5132]">{money(convert(h.monthly_rental, h.currency), displayCur)}</div></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#E5DFD5] flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      {h.is_unlocked ? <span className="text-[#0F5132] font-medium">Unlocked · liquid on secondary</span> : <span>Locked until {new Date(h.lockup_ends_at).toLocaleDateString()}</span>}
                      {h.listed_units > 0 && <span className="ml-2 text-amber-700">{h.listed_units} listed</span>}
                    </div>
                    <ListForSaleDialog holding={h} onListed={load} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {myListings.length > 0 && (
            <>
              <h2 className="font-serif text-2xl text-[#0B192C] mt-12 mb-4">My Secondary Listings</h2>
              <div className="bg-white border border-[#E5DFD5] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3EFEA]"><tr><th className="p-3 text-left">Property</th><th className="p-3 text-right">Units</th><th className="p-3 text-right">Price/unit</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {myListings.map((l) => (
                      <tr key={l.id} className="border-t border-[#E5DFD5]" data-testid={`my-listing-${l.id}`}>
                        <td className="p-3">{l.property_title}</td>
                        <td className="p-3 text-right font-mono">{l.units}</td>
                        <td className="p-3 text-right font-mono">{money(l.price_per_unit, l.currency)}</td>
                        <td className="p-3 text-right font-mono">{money(l.total_price, l.currency)}</td>
                        <td className="p-3 text-center"><Badge variant={l.status === "sold" ? "default" : l.status === "active" ? "secondary" : "outline"}>{l.status}</Badge></td>
                        <td className="p-3 text-right">
                          {l.status === "active" && <Button size="sm" variant="outline" onClick={() => cancelListing(l.id)} data-testid={`cancel-listing-${l.id}`}>Cancel</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </InvestorLayout>
  );
}

export function KycPage() {
  const { refresh, user } = useAuth();
  const [form, setForm] = useState({ full_name: user?.name || "", dob: "", address: "", id_type: "passport", id_number: "" });
  const [file, setFile] = useState(null);
  const [my, setMy] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/kyc/my").then((r) => setMy(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("doc_type", "id_document");
        await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await api.post("/kyc/submit", form);
      toast.success("KYC submitted. Approval usually within 24h.");
      await refresh();
      const { data } = await api.get("/kyc/my"); setMy(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <InvestorLayout active="kyc">
      <div className="overline">Compliance</div>
      <h1 className="font-serif text-4xl text-[#0B192C] mt-2">KYC & Documents</h1>
      {my && <div className="mt-3"><Badge variant={my.status === "approved" ? "default" : "secondary"} data-testid="kyc-status">{my.status}</Badge></div>}
      <Card className="mt-8 max-w-2xl"><CardContent className="pt-6">
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Full legal name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="kyc-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} required data-testid="kyc-dob" /></div>
            <div>
              <Label>ID type</Label>
              <select className="w-full border border-input h-10 rounded-md px-3 text-sm bg-transparent" value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} data-testid="kyc-id-type">
                <option value="passport">Passport</option><option value="drivers_license">Driver's License</option><option value="national_id">National ID</option>
              </select>
            </div>
          </div>
          <div><Label>ID number</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} required data-testid="kyc-id-number" /></div>
          <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required data-testid="kyc-address" /></div>
          <div><Label>Upload ID document (PDF or image, max 10MB)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files[0])} data-testid="kyc-file" />
          </div>
          <Button type="submit" className="btn-gold rounded-full" disabled={loading} data-testid="kyc-submit">
            {loading ? "Submitting…" : "Submit for review"}
          </Button>
        </form>
      </CardContent></Card>
    </InvestorLayout>
  );
}

export function Statements() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portfolio/statements").then((r) => setItems(r.data)); }, []);
  return (
    <InvestorLayout active="statements">
      <div className="overline">Statements</div>
      <h1 className="font-serif text-4xl text-[#0B192C] mt-2">Rental income history.</h1>
      <div className="mt-8 bg-white border border-[#E5DFD5] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFEA]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Property</th><th className="p-3 text-left">Period</th><th className="p-3 text-right">Amount</th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-[#E5DFD5]" data-testid={`statement-${s.id}`}>
                <td className="p-3 text-slate-600">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="p-3">{s.property_title}</td>
                <td className="p-3 text-slate-600 font-mono">{s.period}</td>
                <td className="p-3 text-right font-mono text-[#0F5132]">{money(s.amount)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="4" className="p-10 text-center text-slate-500">No distributions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </InvestorLayout>
  );
}

export function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals/me").then((r) => setData(r.data)); }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_link);
      toast.success("Link copied");
    } catch (e) {
      // Fallback for restricted clipboard contexts
      const ta = document.createElement("textarea");
      ta.value = data.referral_link; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Link copied"); }
      catch { toast.error("Copy failed — long-press to copy manually"); }
      document.body.removeChild(ta);
    }
  };
  if (!data) return <InvestorLayout active="referrals"><div>Loading…</div></InvestorLayout>;
  return (
    <InvestorLayout active="referrals">
      <div className="overline">Referral Hub</div>
      <h1 className="font-serif text-4xl text-[#0B192C] mt-2">Earn 5% on every referred investment.</h1>
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <div className="bg-white border border-[#E5DFD5] rounded-xl p-6"><div className="overline">Referral Code</div><div className="metric-value text-3xl mt-2 text-[#0B192C]" data-testid="ref-code">{data.referral_code}</div></div>
        <div className="bg-white border border-[#E5DFD5] rounded-xl p-6"><div className="overline">Total Referrals</div><div className="metric-value text-3xl mt-2 text-[#0B192C]">{data.total_referrals}</div></div>
        <div className="bg-white border border-[#E5DFD5] rounded-xl p-6"><div className="overline">Commission Earned</div><div className="metric-value text-3xl mt-2 text-[#0F5132]">{money(data.total_earned)}</div></div>
      </div>
      <div className="mt-6 flex gap-3">
        <Input readOnly value={data.referral_link} data-testid="ref-link" />
        <Button className="btn-gold rounded-full" onClick={copy} data-testid="ref-copy">Copy</Button>
      </div>
      <h2 className="font-serif text-2xl text-[#0B192C] mt-10 mb-3">Referred investors</h2>
      <div className="bg-white border border-[#E5DFD5] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFEA]"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Joined</th></tr></thead>
          <tbody>
            {data.referrals.map((r, i) => (<tr key={i} className="border-t border-[#E5DFD5]"><td className="p-3">{r.name}</td><td className="p-3">{r.email}</td><td className="p-3 text-slate-600">{new Date(r.joined_at).toLocaleDateString()}</td></tr>))}
            {data.referrals.length === 0 && <tr><td colSpan="3" className="p-10 text-center text-slate-500">Share your link to start earning.</td></tr>}
          </tbody>
        </table>
      </div>
    </InvestorLayout>
  );
}

export function Withdrawals() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState("");
  const load = async () => { const { data } = await api.get("/withdrawals/my"); setItems(data); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/withdrawals", { amount: parseFloat(amount), method: "bank", account_details: account });
      toast.success("Withdrawal request submitted");
      setAmount(""); setAccount(""); load(); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <InvestorLayout active="withdrawals">
      <div className="overline">Payouts</div>
      <h1 className="font-serif text-4xl text-[#0B192C] mt-2">Withdrawal requests.</h1>
      <div className="mt-3 text-slate-600">Available balance: <span className="font-mono text-[#0F5132]" data-testid="wd-available">{money(user?.wallet_balance)}</span></div>
      <Card className="mt-6 max-w-lg"><CardContent className="pt-6">
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Amount (USD)</Label><Input type="number" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="wd-amount" /></div>
          <div><Label>Bank account details</Label><Textarea placeholder="Bank name, account number, routing / IBAN" value={account} onChange={(e) => setAccount(e.target.value)} required data-testid="wd-account" /></div>
          <Button type="submit" className="btn-gold rounded-full" data-testid="wd-submit">Request payout</Button>
        </form>
      </CardContent></Card>
      <h2 className="font-serif text-2xl text-[#0B192C] mt-10 mb-3">History</h2>
      <div className="bg-white border border-[#E5DFD5] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFEA]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-right">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-[#E5DFD5]" data-testid={`wd-row-${w.id}`}>
                <td className="p-3 text-slate-600">{new Date(w.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right font-mono">{money(w.amount)}</td>
                <td className="p-3 text-center"><Badge variant={w.status === "approved" ? "default" : w.status === "rejected" ? "destructive" : "secondary"}>{w.status}</Badge></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="3" className="p-10 text-center text-slate-500">No withdrawal requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </InvestorLayout>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);
  const sid = params.get("session_id");
  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status !== "paid" && tries < 10) setTimeout(() => setTries((t) => t + 1), 2000);
      } catch (e) {}
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, tries]);
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6">
      <Card className="max-w-lg w-full"><CardContent className="pt-10 pb-10 text-center">
        {status.payment_status === "paid" ? (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
            <h1 className="font-serif text-3xl text-[#0B192C] mt-4">Investment confirmed</h1>
            <p className="text-slate-600 mt-2">{money(status.amount)} in {status.property_title}</p>
            <Link to="/portfolio"><Button className="btn-gold rounded-full mt-6" data-testid="ps-portfolio-btn">View portfolio</Button></Link>
          </>
        ) : (
          <>
            <Clock className="h-14 w-14 text-amber-500 mx-auto animate-pulse" />
            <h1 className="font-serif text-3xl text-[#0B192C] mt-4">Confirming payment…</h1>
            <p className="text-slate-600 mt-2">This usually takes a few seconds.</p>
          </>
        )}
      </CardContent></Card>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6">
      <Card className="max-w-lg w-full"><CardContent className="pt-10 pb-10 text-center">
        <XCircle className="h-14 w-14 text-slate-400 mx-auto" />
        <h1 className="font-serif text-3xl text-[#0B192C] mt-4">Payment cancelled</h1>
        <p className="text-slate-600 mt-2">No charges were made.</p>
        <Link to="/marketplace"><Button className="btn-gold rounded-full mt-6">Back to marketplace</Button></Link>
      </CardContent></Card>
    </div>
  );
}
